/** Wording shared by the AI briefing's screens (docs/plans/ai-briefings-plan.md). */

export const FEATURE_KEY = 'ai_briefings'
export const FEATURE_NAME = 'AI Briefings'
export const BUTTON_LABEL = 'AI briefing'
export const AI_USAGE_PATH = '/admin/ai'

/** The guard's refusals in the panel's words (section 6). */
export const REFUSAL_WORDS: Readonly<Record<string, string>> = {
  cooldown: 'A new briefing can be written a few minutes after the last',
  nothing_new: 'Nothing new since this briefing',
  questions_off: 'Questions are switched off for your organization',
  questions_used: 'The questions for this briefing are used up',
  question_pending: 'A question is being answered',
  question_too_short: 'Ask a little more',
  question_too_long: 'That question is too long',
  nothing_to_ask: 'There is nothing in this briefing to ask about',
  already_emailed: 'This briefing was emailed already',
  briefing_empty: 'An empty briefing is not emailed',
  briefing_not_ready: 'The briefing is not ready yet',
  email_off: 'Emailing briefings is switched off for your organization',
  daily_cap: 'Your briefings for today are used up',
  client_cap: "Your organization's AI calls for today are used up",
  deployment_cap: 'The AI briefings are resting until tomorrow',
}

/** "Mon, Oct 27, 9:14 AM" in the browser's zone. */
export function whenLabel(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** "9:14 AM" for a time today, else the date too. */
export function timeLabel(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return iso
  }
  const today = new Date().toDateString() === date.toDateString()
  return today ? date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : whenLabel(iso)
}

/** Seconds until an instant, never below zero. */
export function secondsUntil(iso: string | null, now: number): number {
  return iso ? Math.max(0, Math.ceil((Date.parse(iso) - now) / 1000)) : 0
}

/** "6 min" or "40 s". */
export function countdownLabel(seconds: number): string {
  return seconds >= 60 ? `${Math.ceil(seconds / 60)} min` : `${seconds} s`
}

export function usdLabel(value: number | null): string {
  return value === null ? '—' : value < 0.01 && value > 0 ? '< $0.01' : `$${value.toFixed(2)}`
}

/** The spans an Insights briefing summarises, as the choice cards name them. */
export const INSIGHTS_SPANS = ['month', 'quarter', 'year'] as const
export type InsightsSpan = (typeof INSIGHTS_SPANS)[number]
export const SPAN_WORDS: Readonly<Record<InsightsSpan, { label: string; detail: string }>> = {
  month: { label: 'This month', detail: 'The month to date: where the figures stand and what moved since last month.' },
  quarter: { label: 'This quarter', detail: 'The calendar quarter to date, with the last ninety days of Bridge callouts and messages.' },
  year: { label: 'This year', detail: 'The year to date, every month added up.' },
}
